export type RoomSource = 'panel' | 'score' | 'composition' | 'effect' | 'stream' | 'remote' | string;

export interface TakeoverTarget {
  source: 'panel' | 'score' | 'effect';
  /** Present-tense phrase that completes “This stops … and …”. */
  action: string;
}

export interface ActiveRoomSource {
  source?: RoomSource | null;
  label?: string | null;
}

export interface TakeoverPrompt {
  title: string;
  detail: string;
  confirmLabel: string;
  activeSource: string;
  targetSource: TakeoverTarget['source'];
}

function sourceCategory(value: RoomSource | null | undefined): string {
  const source = String(value ?? '').trim().toLowerCase();
  if (!source || source === 'idle' || source === 'held') return 'panel';
  if (source === 'remote' || source === 'remote_live' || source === 'remote-live') return 'stream';
  return source;
}

function fallbackLabel(source: string): string {
  return ({
    panel: 'Scenes',
    score: 'Score',
    // The console no longer authors compositions, but a controller that still runs one owns the room.
    // The operator is told what it is in their words, never in the daemon's.
    composition: 'a saved look',
    effect: 'Effect',
    stream: 'Nuance stream',
  } as Record<string, string>)[source] ?? 'the current look';
}

/**
 * Build the console's one category-handoff decision function.
 *
 * Screens call only the returned `confirmTakeover(target)`. The current source is read at click time;
 * same-category switches proceed immediately, while a category change delegates to the one global modal.
 */
export function createTakeoverConfirmation(
  currentSource: () => ActiveRoomSource,
  requestConfirmation: (prompt: TakeoverPrompt) => Promise<boolean>,
): (target: TakeoverTarget) => Promise<boolean> {
  return async function confirmTakeover(target: TakeoverTarget): Promise<boolean> {
    const current = currentSource();
    const activeSource = sourceCategory(current.source);
    // Owner, 2026-09-23: an effect replaces a running effect (Auto VJ included) with no prompt; only a
    // change of category — effect ↔ scene / score — asks (CONTRACT-console B62).
    if (activeSource === sourceCategory(target.source)) return true;

    const activeSourceLabel = String(current.label ?? '').trim() || fallbackLabel(activeSource);
    const action = target.action.trim().replace(/[.!?]+$/, '');
    return requestConfirmation({
      title: `Take over from ${activeSourceLabel}?`,
      detail: `This stops ${activeSourceLabel} and ${action}. Continue?`,
      confirmLabel: 'Take over',
      activeSource,
      targetSource: target.source,
    });
  };
}
